declare global {
  interface Window {
    // OO_GLOBALS is deprecated. Can be removed once the SPA
    // is migrated out of cornucopia.
    OO_GLOBALS?: { session?: CornucopiaSession; restaurantGuid: string }
    __TOAST_BANQUET_INITIAL_DATA__?: {
      session?: PublicBanquetSession
      restaurantGuid?: string
    }
  }
}

interface CornucopiaSession {
  id?: string
  issuedAt?: number // Unix timestamp (seconds since epoch)
  expiresAt?: number // Unix timestamp (seconds since epoch)
}

interface PublicBanquetSession {
  id?: string
  issuedAt?: { dateTime: DateTime }
  expiresAt?: { dateTime: DateTime }
}

type DateTime = {
  date: { year: number; month: number; day: number } // month representation is 1-12
  time: { hour: number; minute: number; second: number }
}

/**
 * This Session is an abstraction of the two different session types coming in from
 * the web server, either the Cornucopia session or the Public Banquet session.
 * We're migrating corn-catering-oo-web from Cornucopia to Public Banquet so we need
 * to support both session objects in the meantime.
 */
export interface Session {
  id?: string
  issuedAt?: Date
  expiresAt?: Date
}

/**
 * The injected session from Public Banquet has dates in a java OffsetDateTime
 * format. Months are respresented as 1-12, and the time is in UTC. This
 * function converts that to a JavaScript Date object.
 */
const utcDateTimeToJsDate = (dateTime?: DateTime) => {
  if (!dateTime) return undefined
  return new Date(
    Date.UTC(
      dateTime.date.year,
      dateTime.date.month - 1, // Date month is 0-indexed
      dateTime.date.day,
      dateTime.time.hour,
      dateTime.time.minute,
      dateTime.time.second
    )
  )
}

/**
 * The injected session from Cornucopia has dates as Unix timestamp (seconds
 * since epoch). This function converts that to a JavaScript Date object.
 */
const unixTimestampToJsDate = (timestamp?: number) => {
  if (!timestamp) return undefined
  return new Date(timestamp * 1000) // Date requires milliseconds
}

/** This function is used for test setup in the BanquetApp story file */
export const jsDateToBanquetDateTime = (date: Date): DateTime => {
  return {
    date: {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth() + 1, // Date month is 0-indexed
      day: date.getUTCDate()
    },
    time: {
      hour: date.getUTCHours(),
      minute: date.getUTCMinutes(),
      second: date.getUTCSeconds()
    }
  }
}

export const getSessionFromWindow = (): Session => {
  if (window.OO_GLOBALS) {
    return {
      ...window.OO_GLOBALS.session,
      issuedAt: unixTimestampToJsDate(window.OO_GLOBALS.session?.issuedAt),
      expiresAt: unixTimestampToJsDate(window.OO_GLOBALS.session?.expiresAt)
    }
  }

  return {
    ...window.__TOAST_BANQUET_INITIAL_DATA__?.session,
    issuedAt: utcDateTimeToJsDate(
      window.__TOAST_BANQUET_INITIAL_DATA__?.session?.issuedAt?.dateTime
    ),
    expiresAt: utcDateTimeToJsDate(
      window.__TOAST_BANQUET_INITIAL_DATA__?.session?.expiresAt?.dateTime
    )
  }
}

export const getSessionId = () => {
  return (
    window.OO_GLOBALS?.session?.id ||
    window.__TOAST_BANQUET_INITIAL_DATA__?.session?.id
  )
}

export const getRestaurantGuidFromWindow = () => {
  return (
    window.__TOAST_BANQUET_INITIAL_DATA__?.restaurantGuid ||
    window.OO_GLOBALS!!.restaurantGuid
  )
}
